// Where a sender's mail goes (U8 "See less"), shared by the server and the web (no runtime deps).
// A destination is local to MeeMail: Gmail is never touched. Brief-only mail waits for one chosen
// brief (its delivery); receipts stay searchable but never become a card or a brief item.
import { WEEKDAYS, type WeekdayKey as Weekday } from "./weekdays";

export const DESTINATIONS = ["cards", "brief", "receipts", "screen_out"] as const;
export type Destination = (typeof DESTINATIONS)[number];

export const DESTINATION_LABEL: Record<Destination, string> = {
  cards: "Cards",
  brief: "Brief only",
  receipts: "Receipts",
  screen_out: "Screen out",
};

/** One plain line under each choice. */
export const DESTINATION_HINT: Record<Destination, string> = {
  cards: "needs me: their mail can become a card",
  brief: "shows up in one brief, never as a card",
  receipts: "kept and searchable, never a card or a brief item",
  screen_out: "set aside; you can restore it",
};

export function isDestination(value: unknown): value is Destination {
  return typeof value === "string" && (DESTINATIONS as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------------------------
// Delivery: which brief brief-only mail shows up in.
//   "next"         the next brief, whenever it is
//   "daily@08:30"  the 8:30am brief (every day)
//   "sat@08:30"    the Saturday 8:30am brief (once a week)
// ---------------------------------------------------------------------------------------------

export type Delivery = "next" | `daily@${string}` | `${Weekday}@${string}`;

export const DEFAULT_DELIVERY: Delivery = "next";

/** Held mail older than this goes into the next brief anyway (a slot may have been removed). */
export const HOLD_MAX_DAYS = 8;

/** A brief counts as "the 8:30 brief" from 8:30 until this many minutes later. */
export const SLOT_WINDOW_MINUTES = 15;

const CLOCK = /^([01]\d|2[0-3]):[0-5]\d$/;

const DAY_NAMES: Record<Weekday, string> = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday",
};

export function parseDelivery(value: string | null | undefined): Delivery | null {
  if (!value) return null;
  if (value === "next") return "next";
  const [day, clock, ...rest] = value.split("@");
  if (rest.length || !clock || !CLOCK.test(clock)) return null;
  if (day === "daily" || (WEEKDAYS as readonly string[]).includes(day ?? ""))
    return value as Delivery;
  return null;
}

export function deliveryParts(d: Delivery): {
  kind: "next" | "daily" | "weekly";
  day: Weekday | null;
  clock: string | null;
} {
  if (d === "next") return { kind: "next", day: null, clock: null };
  const [day, clock] = d.split("@") as [string, string];
  return day === "daily"
    ? { kind: "daily", day: null, clock }
    : { kind: "weekly", day: day as Weekday, clock };
}

/** "08:30" → "8:30am"; "13:00" → "1pm". */
export function clockLabel(clock: string): string {
  const [h, m] = clock.split(":").map(Number) as [number, number];
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}${m ? `:${String(m).padStart(2, "0")}` : ""}${h < 12 ? "am" : "pm"}`;
}

/** "the next brief", "the 8:30am brief", "the Saturday 8:30am brief". */
export function deliveryLabel(d: Delivery): string {
  const p = deliveryParts(d);
  if (p.kind === "next") return "the next brief";
  if (p.kind === "daily") return `the ${clockLabel(p.clock!)} brief`;
  return `the ${DAY_NAMES[p.day!]} ${clockLabel(p.clock!)} brief`;
}

export function weekdayName(day: Weekday): string {
  return DAY_NAMES[day];
}

/** Weekday and "HH:MM" of an instant in a time zone. */
export function localSlot(at: Date, timeZone: string): { day: Weekday; clock: string } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(at);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  const day = get("weekday").slice(0, 3).toLowerCase() as Weekday;
  return { day, clock: `${get("hour").padStart(2, "0")}:${get("minute").padStart(2, "0")}` };
}

const minutesOf = (clock: string) => {
  const [h, m] = clock.split(":").map(Number) as [number, number];
  return h * 60 + m;
};

/** Whether a brief at `briefAt` is the one `d` names (always true for "next"). */
export function briefMatches(d: Delivery, briefAt: Date, timeZone: string): boolean {
  const p = deliveryParts(d);
  if (p.kind === "next") return true;
  const slot = localSlot(briefAt, timeZone);
  if (p.kind === "weekly" && slot.day !== p.day) return false;
  const delta = minutesOf(slot.clock) - minutesOf(p.clock!);
  return delta >= 0 && delta < SLOT_WINDOW_MINUTES;
}

/** Whether mail held since `heldAt` for `d` goes into a brief made at `briefAt`. */
export function deliveryDue(d: Delivery, heldAt: string, briefAt: Date, timeZone: string): boolean {
  if (briefMatches(d, briefAt, timeZone)) return true;
  return briefAt.getTime() - new Date(heldAt).getTime() >= HOLD_MAX_DAYS * 86_400_000;
}
